import { useParams, useLocation, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { groupService } from '../../services/groupService';
import { GroupStatusBadge } from '../../components/common/StatusBadge';
import { useAuth } from '../../hooks/useAuth';
import { formatCurrency, formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const CYCLE_STATUS_META = {
  released:   { label: 'Released',   bg: 'rgba(0,200,120,0.12)',  color: '#00C878' },
  ready:      { label: 'Ready',      bg: 'rgba(21,150,224,0.12)', color: '#1596E0' },
  collecting: { label: 'Collecting', bg: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' },
  short:      { label: 'Short',      bg: 'rgba(255,153,0,0.12)',  color: '#F5A623' },
  pending:    { label: 'Pending',    bg: 'rgba(255,255,255,0.06)', color: 'var(--text-3)' },
  skipped:    { label: 'Skipped',    bg: 'rgba(255,71,87,0.12)',  color: '#FF4757' },
};

function CyclePill({ status }) {
  const m = CYCLE_STATUS_META[status] || CYCLE_STATUS_META.pending;
  return <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: '0.65rem', fontWeight: 700, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>{m.label}</span>;
}

function ContributeModal({ group, cycle, onClose }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState(String(group.contribution_amount));
  const [method, setMethod] = useState('bank_transfer');
  const [err, setErr] = useState('');

  const mutation = useMutation({
    mutationFn: (payload) => groupService.contribute(group.id, cycle.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['group-cycles', group.id] });
      qc.invalidateQueries({ queryKey: ['group-detail', group.id] });
      onClose();
    },
    onError: (e) => setErr(e.response?.data?.message || 'Contribution failed.'),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Contribute to Cycle #{cycle.cycle_number}</h2>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--text-2)' }}>Recipient</span>
            <span style={{ fontWeight: 700 }}>{cycle.recipient_name}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span style={{ color: 'var(--text-2)' }}>Pot So Far</span>
            <span style={{ fontWeight: 700, color: '#00d9a5' }}>{formatCurrency(cycle.collected_amount)} / {formatCurrency(cycle.expected_pot_amount)}</span>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Amount (₦)</label>
          <input className="form-input" type="number" min="1" value={amount} onChange={e => setAmount(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Payment Method</label>
          <select className="form-input" value={method} onChange={e => setMethod(e.target.value)}>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="card">Card Payment</option>
            <option value="ussd">USSD</option>
            <option value="direct_debit">Direct Debit</option>
          </select>
        </div>
        {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={mutation.isPending || !amount || parseFloat(amount) <= 0} onClick={() => mutation.mutate({ amount: parseFloat(amount), method })}>
            {mutation.isPending ? 'Processing…' : 'Confirm Contribution'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function InviteModal({ group, onClose }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);

  const mutation = useMutation({
    mutationFn: () => groupService.inviteMember(group.id, { email }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['group-detail', group.id] }); setSent(true); setEmail(''); },
    onError: (e) => setErr(e.response?.data?.message || 'Could not send invite.'),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 400 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Invite a Member</h2>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <span>ℹ</span><span>Share invite code <strong>{group.invite_code}</strong> directly, or send an email invite below.</span>
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="member@example.com" />
        </div>
        {sent && <div className="alert alert-success" style={{ marginBottom: '0.75rem' }}><span>✓</span><span>Invite sent.</span></div>}
        {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={mutation.isPending || !email.trim()} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Sending…' : 'Send Invite'}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function ReportIssueModal({ group, onClose }) {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);

  const mutation = useMutation({
    mutationFn: () => groupService.reportDispute(group.id, { reason }),
    onSuccess: () => setSent(true),
    onError: (e) => setErr(e.response?.data?.message || 'Could not submit report.'),
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Report an Issue</h2>
          <button className="btn btn-ghost" style={{ padding: '0.25rem 0.5rem' }} onClick={onClose}>✕</button>
        </div>
        {sent ? (
          <div className="alert alert-success"><span>✓</span><span>Reported. Our team will review it.</span></div>
        ) : (
          <>
            <div className="form-group">
              <label className="form-label">Describe the issue</label>
              <textarea className="form-input" rows={4} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Member hasn't paid and won't respond" />
            </div>
            {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button className="btn btn-primary" style={{ flex: 1 }} disabled={mutation.isPending || !reason.trim()} onClick={() => mutation.mutate()}>
                {mutation.isPending ? 'Submitting…' : 'Submit Report'}
              </button>
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function GroupDetail() {
  const { id: groupId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [modal, setModal] = useState(null);
  const [successMsg, setSuccessMsg] = useState(location.state?.success || '');

  const { data: group, isLoading: groupLoading } = useQuery({
    queryKey: ['group-detail', groupId],
    queryFn: () => groupService.getGroup(groupId).then(r => r.data.data),
  });

  const { data: cyclesData, isLoading: cyclesLoading } = useQuery({
    queryKey: ['group-cycles', groupId],
    queryFn: () => groupService.getCycles(groupId).then(r => r.data.data),
    enabled: !!group && group.status !== 'forming',
  });

  const startMutation = useMutation({
    mutationFn: () => groupService.startRotation(groupId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['group-detail', groupId] });
      qc.invalidateQueries({ queryKey: ['group-cycles', groupId] });
      setSuccessMsg('Rotation started!');
    },
  });

  if (groupLoading) return <LoadingSpinner />;
  if (!group) return <p style={{ color: 'var(--text-3)' }}>Group not found.</p>;

  const isOrganizer = group.organizer === user?.id;
  const cycles = cyclesData?.results || [];
  const members = [...(group.members || [])].sort((a, b) => a.rotation_position - b.rotation_position);
  const canStart = isOrganizer && group.status === 'forming' && group.member_count >= group.max_members;

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginBottom: '0.2rem' }}>
            <Link to="/groups" style={{ color: 'var(--text-3)', textDecoration: 'none' }}>← My Groups</Link>
          </div>
          <h1>{group.name}</h1>
          <p>{group.reference}</p>
        </div>
        <GroupStatusBadge status={group.status} />
      </div>

      {successMsg && <div className="alert alert-success" style={{ marginBottom: '1rem' }}><span>✓</span><span>{successMsg}</span></div>}

      <div className="grid-3" style={{ marginBottom: '1.25rem' }}>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(group.contribution_amount)}</div>
          <div className="stat-label">Per Member / {group.cycle}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{group.member_count}/{group.max_members}</div>
          <div className="stat-label">Members</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ fontFamily: 'monospace' }}>{group.invite_code}</div>
          <div className="stat-label">Invite Code</div>
        </div>
      </div>

      {group.status === 'forming' && (
        <div className="alert alert-info" style={{ marginBottom: '1.25rem' }}>
          <span>ℹ</span>
          <span>
            Waiting for members to join ({group.member_count}/{group.max_members}).
            {canStart ? ' Everyone has joined — you can start the rotation now.' : isOrganizer ? ' Invite more members or share the invite code above.' : ' The organizer will start the rotation once everyone joins.'}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {isOrganizer && group.status === 'forming' && (
          <button className="btn btn-ghost" onClick={() => setModal({ type: 'invite' })}>Invite Member</button>
        )}
        {canStart && (
          <button className="btn btn-primary" disabled={startMutation.isPending} onClick={() => startMutation.mutate()}>
            {startMutation.isPending ? 'Starting…' : 'Start Rotation'}
          </button>
        )}
        {group.status === 'active' && (
          <button className="btn btn-ghost" onClick={() => setModal({ type: 'report' })}>Report an Issue</button>
        )}
      </div>

      <div className="grid-2" style={{ alignItems: 'start', gap: '1.25rem' }}>
        <div className="card">
          <h2 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '1rem' }}>Rotation Order</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {members.map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: 'var(--surface-alt)', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <span style={{ width: 24, height: 24, borderRadius: '50%', background: m.has_received_payout ? '#00C878' : 'rgba(21,150,224,0.15)', color: m.has_received_payout ? '#fff' : '#1596E0', fontSize: '0.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {m.has_received_payout ? '✓' : m.rotation_position}
                  </span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{m.user_name}</span>
                </div>
                {m.has_received_payout && <span style={{ fontSize: '0.65rem', color: '#00C878', fontWeight: 700 }}>PAID OUT</span>}
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h2 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '1rem' }}>Cycle Schedule</h2>
          {cyclesLoading ? <LoadingSpinner /> : cycles.length === 0 ? (
            <p style={{ color: 'var(--text-3)', fontSize: '0.85rem' }}>Rotation hasn't started yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {cycles.map(c => {
                const myMembership = members.find(m => m.user === user?.id);
                const alreadyPaid = c.contributions?.some(ct => ct.payer_name === myMembership?.user_name);
                return (
                  <div key={c.id} style={{ padding: '0.625rem 0.75rem', background: 'var(--surface-alt)', borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Cycle #{c.cycle_number} → {c.recipient_name}</span>
                      <CyclePill status={c.status} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-3)' }}>
                      <span>Due {formatDate(c.due_date)}</span>
                      <span>{formatCurrency(c.collected_amount)} / {formatCurrency(c.expected_pot_amount)}</span>
                    </div>
                    {['pending', 'collecting', 'short'].includes(c.status) && !alreadyPaid && (
                      <button className="btn btn-primary" style={{ fontSize: '0.7rem', padding: '0.25rem 0.6rem', marginTop: '0.5rem' }} onClick={() => setModal({ type: 'contribute', cycle: c })}>
                        Contribute
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {modal?.type === 'contribute' && <ContributeModal group={group} cycle={modal.cycle} onClose={() => setModal(null)} />}
      {modal?.type === 'invite' && <InviteModal group={group} onClose={() => setModal(null)} />}
      {modal?.type === 'report' && <ReportIssueModal group={group} onClose={() => setModal(null)} />}
    </div>
  );
}
